import { Trans, useTranslation, type TFunction } from '@mint-lib/i18n';
import { WarningAltFilled } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications';
import {
  ConfirmationDialog,
  ConfirmationDialogAction,
  ConfirmationDialogCancel,
  ConfirmationDialogContent,
  ConfirmationDialogDescription,
  ConfirmationDialogFooter,
  ConfirmationDialogHeader,
  ConfirmationDialogOverlay,
  ConfirmationDialogPortal,
  ConfirmationDialogRoot,
  ConfirmationDialogTitle,
} from '@mint-lib/tw-components';
import { MediaPlan } from '@mint-sdk/api-media-plan';

type DeleteMediaDataModalProps = {
  mediaData: MediaPlan.MediaData | null;
  onClose: () => void;
};

export const DeleteMediaDataModal = ({
  mediaData,
  onClose,
}: DeleteMediaDataModalProps) => {
  const { t } = useTranslation('@myn/planning_v2');

  const { mutateAsync: deleteMediaItem } = MediaPlan.deleteMediaItem();

  const handleMediaDataDelete = async () => {
    onClose();

    if (!mediaData) {
      return;
    }

    try {
      await deleteMediaItem({
        urlParams: { mediaItemId: mediaData.mediaItemId },
      });
    } catch {
      enqueueSnackbar({
        title: t('Failed to delete row'),
        variant: 'error',
        removable: true,
      });
    }
  };

  const confirmMessage = getConfirmMessage(mediaData, t);

  return (
    <ConfirmationDialog
      open={!!mediaData}
      onOpenChange={(isOpen) => !isOpen && onClose()}
    >
      <ConfirmationDialogPortal>
        <ConfirmationDialogOverlay />

        <ConfirmationDialogRoot>
          <ConfirmationDialogHeader>
            <WarningAltFilled className="fill-error-main" />
            <ConfirmationDialogTitle>
              {confirmMessage?.title}
            </ConfirmationDialogTitle>
          </ConfirmationDialogHeader>

          <ConfirmationDialogContent>
            <ConfirmationDialogDescription>
              {confirmMessage?.description}
            </ConfirmationDialogDescription>
          </ConfirmationDialogContent>

          <ConfirmationDialogFooter>
            <ConfirmationDialogCancel
              onClick={onClose}
              aria-label={t('Cancel delete grid row')}
            />
            <ConfirmationDialogAction
              color="error"
              onClick={handleMediaDataDelete}
              aria-label={t('Submit delete grid row')}
            >
              {t('Yes, delete')}
            </ConfirmationDialogAction>
          </ConfirmationDialogFooter>
        </ConfirmationDialogRoot>
      </ConfirmationDialogPortal>
    </ConfirmationDialog>
  );
};

function getConfirmMessage(
  mediaData: MediaPlan.MediaData | null,
  t: TFunction,
) {
  if (!mediaData) {
    return null;
  }

  const mdLabel = mediaData.label || '';

  if (mediaData.levelType === 'media_row') {
    return {
      title: t('Delete this row?'),
      description: mediaData.mediaRowProps?.isCampaignSettingsAvailable
        ? t(
            'This media row is configured. Deleting it will remove the media row from the Activity and all Flights, together with all related data and items created in other modules.',
          )
        : t(
            "This media row hasn't been configured yet, so no planning data will be lost. It will be removed from the Activity and all Flights.",
          ),
    };
  }

  if (mediaData.levelType === 'activity') {
    return {
      title: t('Delete {{name}}?', { name: mdLabel }),
      description: (
        <Trans
          i18nKey="By proceeding, you will delete the <Bold>{{name}}</Bold>, Its structure and Flights if present."
          components={{ Bold: <span className="font-semibold" /> }}
          values={{ name: mdLabel }}
          t={t}
        />
      ),
    };
  }

  return {
    title: t('Delete {{name}}?', { name: mdLabel }),
    description: (
      <Trans
        i18nKey="By proceeding, you will delete the <Bold>{{name}}</Bold> and Its structure."
        components={{ Bold: <span className="font-semibold" /> }}
        values={{ name: mdLabel }}
        t={t}
      />
    ),
  };
}
